import type { ApiError } from '@/lib/checkout/types';
import { checkoutActions } from '@/states/checkout';

import { localizeApiErrors } from './localizeApiErrors';
import { hasOrderCreateShoppingBagError } from './orderCreateShoppingBagErrorCodes';

export {
  ORDER_CREATE_SHOPPING_BAG_ERROR_CODES,
  hasOrderCreateShoppingBagError,
  isOrderCreateShoppingBagErrorCode,
  type OrderCreateShoppingBagErrorCode,
} from './orderCreateShoppingBagErrorCodes';

export interface OrderCreateErrorInput {
  code: string;
  message: string;
  field?: string | null;
  context?: string | Record<string, unknown> | null;
}

export interface HandleOrderCreateErrorsParams {
  errors: OrderCreateErrorInput[];
  locale: string;
  refreshCheckout?: () => Promise<unknown>;
}

/**
 * Maps orderCreate failures to localized UX. CPI mismatch/stock errors refresh checkout
 * totals and push messages into checkout state (same business flow as Breitling payment).
 */
export async function handleOrderCreateErrors({
  errors,
  locale,
  refreshCheckout,
}: HandleOrderCreateErrorsParams): Promise<{ localizedErrors: ApiError[]; userMessage: string }> {
  const rawApiErrors: ApiError[] = errors.map((error) => {
    let context: Record<string, unknown> | undefined;
    if (typeof error.context === 'string' && error.context) {
      try {
        context = JSON.parse(error.context) as Record<string, unknown>;
      } catch {
        context = undefined;
      }
    } else if (error.context && typeof error.context === 'object') {
      context = error.context;
    }

    return {
      code: error.code,
      message: error.message,
      field: error.field ?? null,
      ...(context ? { context } : {}),
    };
  });

  const localizedErrors = localizeApiErrors(rawApiErrors, locale);

  if (hasOrderCreateShoppingBagError(errors)) {
    // Refresh first so totals/lines settle, then publish errors — otherwise a scroll-to-error
    // that runs on setErrors is wiped by the refresh layout update.
    if (refreshCheckout) {
      await refreshCheckout();
    }
    checkoutActions.clearErrors();
    checkoutActions.setErrors(localizedErrors, 'orderApp');
  }

  return {
    localizedErrors,
    userMessage: localizedErrors[0]?.message ?? 'Failed to create order',
  };
}
